import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { register, resendConfirmation } from '../api';
import { signupAttribution } from '../measurement';
import styles from './Register.module.css';
import { useLang } from '../i18n/LanguageContext';
import { registerText } from '../i18n/pages/register';

const BookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

const MAX_SLIDER = 20;
const ENTERPRISE_THRESHOLD = 10;
const CONTACT_EMAIL = 'info@guestbook.cz';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Reports the sign-up conversion to Google Ads. Called only after the backend has confirmed the
 * registration, so a rejected or failed submit is never counted. Stays silent when the visitor
 * hasn't granted cookie consent — gtag holds the event rather than storing anything (see the
 * consent defaults in public/index.html).
 */
const reportSignupConversion = () => {
  try {
    window.gtag?.('event', 'conversion', {
      send_to: 'AW-18462774076/tKUaCPbrsoAdELym3uNE',
      value: 1.0,
      currency: 'CZK',
    });
  } catch {
    // Measurement must never break the signup flow.
  }
};

// Czech phone: optional +420/00420/420 prefix, then 9 digits (spaces/dashes allowed)
const CZ_PHONE_RE = /^(\+420|00420|420)?[\s-]?[0-9]{3}[\s-]?[0-9]{3}[\s-]?[0-9]{3}$/;
const ICO_RE = /^\d{8}$/;

function validatePhone(value: string, phoneRequired: string, phoneInvalid: string): string | null {
  if (!value.trim()) return phoneRequired;
  if (!CZ_PHONE_RE.test(value.trim())) return phoneInvalid;
  return null;
}

function validateIco(value: string, icoInvalid: string): string | null {
  if (!value.trim()) return null;
  if (!ICO_RE.test(value.trim())) return icoInvalid;
  return null;
}

const Register: React.FC = () => {
  useEffect(() => { fetch('/api/public/pageview?page=register').catch(() => {}); }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang } = useLang();
  const t = registerText[lang];

  const planParam = searchParams.get('plan')?.toUpperCase();
  const initialCycle = planParam === 'ANNUAL' ? 'ANNUAL' : 'MONTHLY';

  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'ANNUAL'>(initialCycle);
  const [sliderValue, setSliderValue] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [ico, setIco] = useState('');
  const [icoError, setIcoError] = useState<string | null>(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [advertisingConsent, setAdvertisingConsent] = useState(false);
  const [agreeGdpr, setAgreeGdpr] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [gdprError, setGdprError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<null | 'sending' | 'sent' | 'error'>(null);

  const plannedCount = sliderValue >= MAX_SLIDER ? 20 : sliderValue;
  const priceMonthly = 300;
  const priceAnnual = 225;
  const price = billingCycle === 'ANNUAL' ? priceAnnual : priceMonthly;
  const isEnterprise = sliderValue >= ENTERPRISE_THRESHOLD;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const phoneErr = validatePhone(phoneNumber, t.phoneRequired, t.phoneInvalid);
    const icoErr = validateIco(ico, t.icoInvalid);
    setPhoneError(phoneErr);
    setIcoError(icoErr);
    setTermsError(!agreeTerms);
    setGdprError(!agreeGdpr);
    if (phoneErr || icoErr || !agreeTerms || !agreeGdpr) return;

    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      await register(email, password, firstName, lastName, phoneNumber.trim(), ico.trim(), plannedCount, billingCycle, signupAttribution(advertisingConsent));
      // Fire before the alert/navigate below — an alert blocks and navigation unmounts.
      reportSignupConversion();
      alert(t.successAlert);
      navigate('/login');
    } catch (err: any) {
      // The backend's code is a stable, language-independent key (e.g. EMAIL_ALREADY_IN_USE) —
      // never a string to display. err.response.data.message is a server-side-safe fallback for
      // the rare error path that doesn't carry a code yet, not something to localize (issue #88).
      const code = err.response?.data?.code as string | undefined;
      const msg = (code && t.errorCodes[code])
        || err.response?.data?.message
        || err.message
        || 'Registration failed';
      setError(msg);
      setErrorCode(code ?? null);
      if (code === 'EMAIL_PENDING_VERIFICATION') setResendStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('sending');
    try {
      await resendConfirmation(email);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  const sliderPct = ((sliderValue - 1) / (MAX_SLIDER - 1)) * 100;

  return (
    <div className={styles.registerPage}>
      <div className={styles.registerContainer}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}><BookIcon /></div>
          <span className={styles.logoText}>Guestbook<span>.cz</span></span>
        </div>

        <div className={styles.title}>{t.title}</div>
        <div className={styles.subtitle}>{t.subtitle}</div>

        <form onSubmit={handleSubmit}>
          {/* ── Billing cycle toggle ─────────────────────────────── */}
          <div className={styles.sectionLabel}>{t.billingCycleLabel}</div>
          <div className={styles.planToggle}>
            <button
              type="button"
              className={billingCycle === 'MONTHLY' ? styles.planOptionActive : styles.planOption}
              onClick={() => setBillingCycle('MONTHLY')}
            >
              <span className={styles.planName}>{t.monthly}</span>
              <span className={styles.planPrice}>300 CZK / apt</span>
            </button>
            <button
              type="button"
              className={billingCycle === 'ANNUAL' ? styles.planOptionActive : styles.planOption}
              onClick={() => setBillingCycle('ANNUAL')}
            >
              <span className={styles.planName}>{t.annual} <span className={styles.planBadge}>{t.annualBadge}</span></span>
              <span className={styles.planPrice}>225 CZK / apt</span>
            </button>
          </div>

          {/* ── Apartment count slider ───────────────────────────── */}
          <div className={styles.sectionLabel} style={{ marginTop: 18 }}>{t.apartmentsLabel}</div>
          <div className={styles.sliderBox}>
            <div className={styles.sliderCount}>
              {sliderValue >= MAX_SLIDER ? t.apartmentsPlus : sliderValue === 1 ? t.apartment : t.apartments(sliderValue)}
            </div>
            {billingCycle === 'ANNUAL' && (
              <div className={styles.sliderSaving}>{t.annualSaving((sliderValue >= MAX_SLIDER ? 20 : sliderValue) * (priceMonthly - priceAnnual) * 12)}</div>
            )}
            <div className={styles.sliderWrapper}>
              <input
                type="range"
                min={1}
                max={MAX_SLIDER}
                value={sliderValue}
                onChange={e => setSliderValue(Number(e.target.value))}
                className={styles.slider}
                style={{ '--pct': `${sliderPct}%` } as React.CSSProperties}
              />
              <div className={styles.sliderTicks}>
                <span>1</span><span>5</span><span>10</span><span>15</span><span>20+</span>
              </div>
            </div>
            {!isEnterprise && (
              <div className={styles.sliderCost}>
                <strong>{t.estimatedCost(plannedCount * price)}</strong>
                {billingCycle === 'ANNUAL' && (
                  <span className={styles.sliderCostSub}>{t.estimatedAnnual(plannedCount * price * 12)}</span>
                )}
              </div>
            )}
          </div>

          {/* ── Enterprise contact banner (10+ apartments) ──────── */}
          {isEnterprise && (
            <div className={styles.enterpriseBanner}>
              <div className={styles.enterpriseBannerText}>
                <strong>{t.enterpriseTitle}</strong> {t.enterpriseBody}
              </div>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Guestbook%20enterprise%20inquiry&body=Hi%2C%20I%27m%20interested%20in%20managing%20${sliderValue >= MAX_SLIDER ? '20%2B' : sliderValue}%20apartments.`}
                className={styles.enterpriseContactBtn}
              >
                {t.enterpriseContact}
              </a>
              <div className={styles.enterpriseBannerSub}>{t.enterpriseSub}</div>
            </div>
          )}

          {/* ── Name / email / password ──────────────────────────── */}
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t.firstNameLabel}</label>
              <input className={styles.input} type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t.firstNamePlaceholder} required />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t.lastNameLabel}</label>
              <input className={styles.input} type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t.lastNamePlaceholder} required />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t.emailLabel}</label>
            <input className={styles.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} required />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t.passwordLabel}</label>
            <input className={styles.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.passwordPlaceholder} required />
          </div>

          {/* ── Phone number ─────────────────────────────────────── */}
          <div className={styles.formGroup}>
            <label className={styles.label}>{t.phoneLabel}</label>
            <input
              className={`${styles.input} ${phoneError ? styles.inputError : ''}`}
              type="tel"
              value={phoneNumber}
              onChange={e => { setPhoneNumber(e.target.value); setPhoneError(null); }}
              onBlur={() => setPhoneError(validatePhone(phoneNumber, t.phoneRequired, t.phoneInvalid))}
              placeholder={t.phonePlaceholder}
              required
            />
            {phoneError && <span className={styles.fieldError}>{phoneError}</span>}
          </div>

          {/* ── IČO ──────────────────────────────────────────────── */}
          <div className={styles.formGroup}>
            <label className={styles.label}>{t.icoLabel} <span className={styles.labelHint}>{t.icoHint}</span></label>
            <input
              className={`${styles.input} ${icoError ? styles.inputError : ''}`}
              type="text"
              inputMode="numeric"
              value={ico}
              onChange={e => { setIco(e.target.value.replace(/\D/g, '').slice(0, 8)); setIcoError(null); }}
              onBlur={() => setIcoError(validateIco(ico, t.icoInvalid))}
              placeholder="12345678"
            />
            {icoError && <span className={styles.fieldError}>{icoError}</span>}
          </div>

          {/* ── Consent checkboxes ───────────────────────────────── */}
          <div className={styles.checkboxGroup}>
            <label className={`${styles.checkboxLabel} ${termsError ? styles.checkboxLabelError : ''}`}>
              <input type="checkbox" checked={agreeTerms} onChange={e => { setAgreeTerms(e.target.checked); setTermsError(false); }} />
              <span>
                {t.agreeTerms}{' '}
                <a href="https://guestbook.cz/podminky.html" target="_blank" rel="noopener noreferrer">{t.termsLink}</a>
              </span>
            </label>
            {termsError && <span className={styles.fieldError}>{t.termsRequired}</span>}
          </div>

          <div className={styles.checkboxGroup}>
            <label className={`${styles.checkboxLabel} ${gdprError ? styles.checkboxLabelError : ''}`}>
              <input type="checkbox" checked={agreeGdpr} onChange={e => { setAgreeGdpr(e.target.checked); setGdprError(false); }} />
              <span>
                {t.agreeGdpr}{' '}
                <a href="https://guestbook.cz/gdpr.html" target="_blank" rel="noopener noreferrer">{t.gdprLink}</a>
              </span>
            </label>
            {gdprError && <span className={styles.fieldError}>{t.gdprRequired}</span>}
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={advertisingConsent} onChange={e => setAdvertisingConsent(e.target.checked)} />
              <span>{lang === 'cs'
                ? 'Volitelně souhlasím s reklamním měřením Google: uložením identifikátoru kliknutí na 30 dní a odesláním ověření e-mailu do Google Ads (uživatelská data a personalizace reklamy).'
                : 'Optional: I consent to Google advertising measurement: storing click identifiers for 30 days and sending email verification to Google Ads (ad user data and ad personalization).'}</span>
            </label>
            <Link to="/privacy/measurement">{lang === 'cs' ? 'Odvolat souhlas s měřením' : 'Withdraw measurement consent'}</Link>
          </div>

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? t.submittingBtn : t.submitBtn}
          </button>
        </form>

        {errorCode === 'EMAIL_PENDING_VERIFICATION' ? (
          <div className={styles.error}>
            This email is awaiting verification. Check your inbox or{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: 'inherit', padding: 0, fontSize: 'inherit' }}
            >
              {resendStatus === 'sending' ? 'sending…' : resendStatus === 'sent' ? '✓ sent!' : 'resend the confirmation email'}
            </button>
            {resendStatus === 'error' && ' — failed, try again.'}
          </div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : null}

        <div className={styles.footer}>
          {t.hasAccount}{' '}
          <Link to="/login">{t.signIn}</Link>
        </div>
        <div className={`${styles.footer} ${styles.footerQuiet}`}>
          <Link to="/privacy/measurement">{t.privacyLink}</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
